import React, { useEffect, useRef, useState } from "react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";

import { useNavigate, useSearchParams } from "react-router-dom";
import Select from 'react-select';
import { GetCategoryList } from "../services/category.service";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";

import * as AuthServices from '../services/auth.service';
import * as UserServices from '../../user/services/user.service';
import { ROLE, SWAL_SETTINGS, INDIAN_STATES } from "../../../utils/Constants";
import { handleServerValidations, setLocalKey, getUser } from "../../../utils/commonfunction";
import { addProfileData } from "../../../redux/slices/profileslice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Upload, Trash2 } from "lucide-react";


function ProfileSetup() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Dispatch = useDispatch();

  const [submitted, setSubmitted] = useState(false);
  const locationRef = useRef({ latitude: null, longitude: null });
  const fileInputRef = useRef(null);
  // Detect if this is a user (become-advertiser) or advertiser flow
  const [currentUserData, setCurrentUserData] = useState(() => getUser(ROLE.USER) || getUser(ROLE.ADVERTISER) || {});
  const userDataForUser = currentUserData;
  const isUserFlow = userDataForUser && userDataForUser.token && userDataForUser.role === ROLE.USER;

  const [categories, setCategories] = useState([]);

  const validationSchema = Yup.object().shape({
    business_handle: Yup.string()
      .trim()
      .required(t("vaildation.business_handle", "Business Profile Name is required")),
    store_name: Yup.string()
      .required(t("vaildation.store_name")),
    store_address: Yup.string()
      .required(t("vaildation.store_address")),
    state: Yup.string()
      .required(t("validation.state_required", "State is required")),
    gst_number: Yup.string()
      .trim()
      .nullable()
      .test(
        "gst-length",
        t("vaildation.gst_must_number"),
        (value) => !value || value.length === 15
      )
      .test(
        "gst-format",
        t("vaildation.gst_invalid"),
        (value) =>
          !value ||
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.toUpperCase())
      ),
    categoryIds: Yup.array()
      .min(1, "Please select at least one category")
      .required("Categories are required"),
    isAgreementSigned: Yup.boolean()
      .oneOf([true], t("advertiser.business_profile.agreement_required", "You must accept the advertiser agreement"))
      .required(t("advertiser.business_profile.agreement_required", "You must accept the advertiser agreement")),
  });

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: {
      store_image: userDataForUser?.store_image || '',
      store_images: userDataForUser?.store_images && Array.isArray(userDataForUser.store_images)
        ? userDataForUser.store_images.map(img => ({ type: 'existing', url: img }))
        : [],
      business_handle: userDataForUser?.business_handle || '',
      store_name: userDataForUser?.store_name || '',
      store_address: userDataForUser?.store_location?.store_address || userDataForUser?.store_address || '',
      state: userDataForUser?.state || userDataForUser?.store_location?.state || '',
      gst_number: userDataForUser?.gst_number || '',
      categoryIds: userDataForUser?.categoryIds || [],
      shipping_policy: userDataForUser?.shipping_policy || '',
      isAgreementSigned: false,
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      setSubmitted(true);
      let formData = new FormData();
      if (values.store_image instanceof File) {
        formData.append("store_image", values.store_image);
      } else if (values.store_image && typeof values.store_image === 'string' && !values.store_image.startsWith('blob:')) {
        formData.append("store_image", values.store_image);
      }
      const rawHandle = values.business_handle ? values.business_handle.trim() : "";
      const cleanedHandle = rawHandle
        .replace(/^https?:\/\/[^\/]+\/businessprofilename\//i, "")
        .replace(/^businessprofilename\//i, "")
        .replace(/^@+/, "");
      formData.append("business_handle", cleanedHandle);
      formData.append("store_name", values.store_name || "");
      formData.append("store_address", values.store_address || "");
      formData.append("state", values.state || "");
      formData.append("gst_number", values.gst_number || "");
      formData.append("categoryIds", JSON.stringify(values.categoryIds || []));
      formData.append("shipping_policy", values.shipping_policy || "");
      formData.append("isAgreementSigned", values.isAgreementSigned);
      formData.append("latitude", locationRef.current.latitude || "");
      formData.append("longitude", locationRef.current.longitude || "");

      const bannerList = Array.isArray(values.store_images) ? values.store_images : [];
      const existingUrls = bannerList.filter(item => item.type === 'existing').map(item => item.url);
      formData.append("existing_store_images", JSON.stringify(existingUrls));

      bannerList.filter(item => item.type === 'new' && item.file).forEach(item => {
        formData.append("store_images", item.file);
      });

      setSubmitted(false);

      // Choose API based on whether this is a user (become-advertiser flow) or advertiser
      const apiCall = AuthServices.completeBusinessProfile(formData);

      apiCall
        .then((response) => {
          setSubmitted(true);

          if (response.success) {
            if (isUserFlow) {
              const existingUserData = getUser(ROLE.USER);
              const token = existingUserData?.token || response?.data?.token;
              const updatedUserData = { ...existingUserData, ...response.data, token, isBusinessRegistered: true };
              setLocalKey(ROLE.USER, JSON.stringify(updatedUserData));
              Dispatch(addProfileData(updatedUserData));
            } else {
              const existingUserData = getUser(ROLE.ADVERTISER);
              const token = existingUserData?.token || response?.data?.token;
              const updatedUserData = { ...existingUserData, ...response.data, token, isBusinessRegistered: true };
              setLocalKey(ROLE.ADVERTISER, JSON.stringify(updatedUserData));
              Dispatch(addProfileData(updatedUserData));
            }

            Swal.fire({
              icon: "success",
              text: response.message || "Store details updated successfully",
              ...SWAL_SETTINGS,
            });

            navigate(`/advertiser/kyc-details`);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          setSubmitted(true);
          console.log("error =====> ", error);
        });
    },
  });

  useEffect(() => {
    UserServices.profileDetails()
      .then((res) => {
        if (res && res.data) {
          const d = res.data;
          setCurrentUserData((prev) => ({ ...prev, ...d }));

          if (d.store_images && Array.isArray(d.store_images) && (!formik.values.store_images || formik.values.store_images.length === 0)) {
            formik.setFieldValue("store_images", d.store_images.map(img => ({ type: 'existing', url: img })));
          }
          if (!formik.values.business_handle && d.business_handle) {
            formik.setFieldValue("business_handle", d.business_handle);
          }
          if (!formik.values.store_name && d.store_name) {
            formik.setFieldValue("store_name", d.store_name);
          }
          if (!formik.values.store_address && (d.store_location?.store_address || d.store_address)) {
            formik.setFieldValue("store_address", d.store_location?.store_address || d.store_address);
          }
          if (!formik.values.state && (d.state || d.store_location?.state)) {
            formik.setFieldValue("state", d.state || d.store_location?.state);
          }
          if (!formik.values.gst_number && d.gst_number) {
            formik.setFieldValue("gst_number", d.gst_number);
          }
          if ((!formik.values.categoryIds || formik.values.categoryIds.length === 0) && d.categoryIds) {
            formik.setFieldValue("categoryIds", d.categoryIds);
          }
          if (!formik.values.shipping_policy && d.shipping_policy) {
            formik.setFieldValue("shipping_policy", d.shipping_policy);
          }
          if (!formik.values.store_image && d.store_image) {
            formik.setFieldValue("store_image", d.store_image);
          }
        }
      })
      .catch((err) => console.log("Profile fetch err:", err));
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await GetCategoryList({ per_page: 100, page: 1 });
        if (res?.list) {
          const options = res.list.map(cat => ({
            value: cat._id,
            label: cat.title
          }));
          setCategories(options);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, []); // empty dependency → run only once

  return (
    <>
      <section className="py-8 md:py-12 lg:py-16 form-bg">
        <div className="container">
          <h1 className="headings-h1 text-center text-zinc-950 mb-8">
            {t("advertiser.business_profile.heading")}
          </h1>
          <div className="bg-white p-[15px] md:p-[50px] rounded-[10px] max-w-[650px] shadow-[0px_2px_24px_rgba(0,0,0,0.05)] w-full mx-auto border border-zinc-100">
            {userDataForUser?.failed_verification_reason && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r">
                <div className="flex items-start">
                  <div className="ml-1">
                    <h3 className="text-sm font-bold text-red-800">
                      Previous Verification Request Rejected
                    </h3>
                    <div className="mt-1 text-sm text-red-700">
                      <p><strong>Reason:</strong> {userDataForUser.failed_verification_reason}</p>
                    </div>
                    <p className="mt-1 text-xs text-red-600">Please review your business details, make any corrections, and proceed to KYC details.</p>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={formik.handleSubmit}>
              <div className="upload-profile flex flex-col items-center justify-center mb-[30px] md:mb-[50px]">
                <div className="relative w-[89px] h-[89px] border-1 border-red-100 rounded-full">
                  {/* Preview image or default placeholder */}
                  <img
                    src={
                      formik.values.store_image
                        ? (typeof formik.values.store_image === 'string' ? formik.values.store_image : URL.createObjectURL(formik.values.store_image))
                        : (userDataForUser?.store_image || "/images/store.png")
                    }
                    alt="store"
                    className="w-full h-full object-cover rounded-full"
                  />

                  <button type="button" className="absolute bottom-[-15px] left-0 w-full flex items-center justify-center"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <img src="/images/upload1.svg" alt="upload" />
                  </button>

                  {/* Hidden input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    name="store_image"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        formik.setFieldValue("store_image", e.target.files[0]); // set file
                        formik.setFieldTouched("store_image", true);
                      }
                    }}
                  />
                </div>
                {formik.errors.store_image && formik.touched.store_image && (
                  <span className="text-danger text-center d-block err-spn mt-4">
                    {formik.errors.store_image}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="businessHandle">{t("advertiser.business_profile.business_handle", "Business Profile Name")}</label>
                <div className="flex items-center form-control-custom !p-0 overflow-hidden focus-within:!border-[var(--secondary-500,#FF63B5)]">
                  <span className="bg-zinc-100 text-zinc-600 text-xs sm:text-sm px-3.5 h-full flex items-center border-r border-zinc-200 select-text cursor-text whitespace-nowrap font-normal" style={{ userSelect: "text" }}>
                    {window.location.origin}/businessprofilename/
                  </span>
                  <input
                    id="businessHandle"
                    type="text"
                    placeholder={t("placeholder.business_handle", "Enter business profile name")}
                    className="w-full h-full bg-transparent px-3 text-zinc-900 outline-none border-none shadow-none text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-0"
                    name="business_handle"
                    value={formik.values.business_handle}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <span className="text-danger text-left d-block err-spn">
                  {formik.errors.business_handle && formik.touched.business_handle && formik.errors.business_handle}
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="storeName">{t("advertiser.business_profile.store_name")}</label>
                <input
                  id="storeName"
                  type="text"
                  placeholder={t("placeholder.store_name")}
                  className="form-control-custom"
                  // required
                  name="store_name"
                  value={formik.values.store_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.store_name && formik.touched.store_name && formik.errors.store_name}
                </span>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="location">{t("advertiser.business_profile.location")}</label>
                {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                  <GooglePlacesAutocomplete
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    selectProps={{
                      value: formik.values.store_address ? { label: formik.values.store_address, value: formik.values.store_address } : null,
                      onChange: (val) => {
                        formik.setFieldValue("store_address", val.label);
                        if (window.google) {
                          const geocoder = new window.google.maps.Geocoder();
                          const query = val?.value?.place_id
                            ? { placeId: val.value.place_id }
                            : { address: val.label };

                          geocoder.geocode(query, (results, status) => {
                            if (status === "OK" && results && results[0]) {
                              const lat = results[0].geometry.location.lat();
                              const lng = results[0].geometry.location.lng();
                              locationRef.current = { latitude: lat, longitude: lng };

                              const stateComp = results[0].address_components?.find((comp) =>
                                comp.types.includes("administrative_area_level_1")
                              );
                              if (stateComp) {
                                const foundState = INDIAN_STATES.find(
                                  (s) =>
                                    s.toLowerCase() === stateComp.long_name.toLowerCase() ||
                                    s.toLowerCase() === stateComp.short_name.toLowerCase()
                                );
                                if (foundState) {
                                  formik.setFieldValue("state", foundState);
                                }
                              }
                            }
                          });
                        }
                      },
                      placeholder: t("placeholder.location"),
                      classNamePrefix: "react-select",
                      styles: {
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: "#f9fafb",
                          borderColor: "#e5e7eb",
                          borderRadius: "8px",
                          padding: "2px",
                          boxShadow: "none",
                          "&:hover": {
                            borderColor: "#d1d5db",
                          },
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      },
                    }}
                  />
                ) : (
                  <input
                    id="location"
                    type="text"
                    placeholder={t("placeholder.location")}
                    className="form-control-custom"
                    name="store_address"
                    value={formik.values.store_address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                )}
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.store_address && formik.touched.store_address && formik.errors.store_address}
                </span>
              </div>

              <div className="form-group mb-3">
                <label htmlFor="state">{t("advertiser.business_profile.state", "State")}</label>
                <Select
                  id="state"
                  name="state"
                  options={INDIAN_STATES.map((st) => ({ value: st, label: st }))}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder={t("placeholder.select_state", "Select State")}
                  value={formik.values.state ? { label: formik.values.state, value: formik.values.state } : null}
                  onChange={(selectedOption) => {
                    formik.setFieldValue("state", selectedOption ? selectedOption.value : "");
                  }}
                  onBlur={() => formik.setFieldTouched("state", true)}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: "#f9fafb",
                      borderColor: "#e5e7eb",
                      borderRadius: "8px",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#d1d5db",
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.state && formik.touched.state && formik.errors.state}
                </span>
              </div>


              <div className="form-group">
                <label htmlFor="gstNumber">{t("advertiser.business_profile.gst_number")}</label>
                <input
                  id="gstNumber"
                  type="text"
                  placeholder={t("placeholder.gst_number")}
                  className="form-control-custom"
                  name="gst_number"
                  value={formik.values.gst_number}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                    formik.setFieldValue("gst_number", val);
                  }}
                  onBlur={formik.handleBlur}
                  maxLength={15}
                  style={{ textTransform: "uppercase" }}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.gst_number && formik.touched.gst_number && formik.errors.gst_number}
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="categoryIds">Product Categories</label>
                <Select
                  id="categoryIds"
                  isMulti
                  name="categoryIds"
                  options={categories}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Categories"
                  value={categories.filter(option => formik.values.categoryIds.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    formik.setFieldValue("categoryIds", values);
                  }}
                  onBlur={() => formik.setFieldTouched("categoryIds", true)}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: "#f9fafb",
                      borderColor: "#e5e7eb",
                      borderRadius: "8px",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#d1d5db",
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.categoryIds && formik.touched.categoryIds && formik.errors.categoryIds}
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="shippingPolicy">Shipping and Handling Policy</label>
                <textarea
                  id="shippingPolicy"
                  rows={4}
                  placeholder="Describe your shipping and handling policy..."
                  className="form-control-custom"
                  style={{ resize: "vertical" }}
                  name="shipping_policy"
                  value={formik.values.shipping_policy}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.shipping_policy && formik.touched.shipping_policy && formik.errors.shipping_policy}
                </span>
              </div>

              <div className="form-group mb-6">
                <label className="block text-sm font-semibold text-zinc-800 mb-1">
                  Store Banner Images (Multiple) <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-zinc-500 mb-3">
                  Upload high-resolution photos of your store to display as banners on your store profile.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-2">
                  {(Array.isArray(formik.values.store_images) ? formik.values.store_images : []).map((item, idx) => (
                    <div key={idx} className="relative group aspect-video bg-zinc-100 rounded-lg overflow-hidden border border-zinc-200 shadow-sm">
                      <img
                        src={item.type === 'existing' ? item.url : item.preview}
                        alt={`Store Banner ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(formik.values.store_images) ? formik.values.store_images : [];
                          const updated = current.filter((_, i) => i !== idx);
                          formik.setFieldValue("store_images", updated);
                          formik.setFieldTouched("store_images", true);
                        }}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-all cursor-pointer"
                        title="Remove image"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-zinc-300 hover:border-[#FF63B5] rounded-lg cursor-pointer bg-zinc-50 hover:bg-pink-50/40 transition-all text-center p-3">
                    <Upload className="w-6 h-6 text-zinc-400 mb-1" />
                    <span className="text-xs font-semibold text-zinc-700">Upload Banners</span>
                    <span className="text-[10px] text-zinc-400">Multiple files supported</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        if (files.length > 0) {
                          const newItems = files.map(file => ({
                            type: 'new',
                            file,
                            preview: URL.createObjectURL(file)
                          }));
                          const current = Array.isArray(formik.values.store_images) ? formik.values.store_images : [];
                          const updated = [...current, ...newItems];
                          formik.setFieldValue("store_images", updated);
                          formik.setFieldTouched("store_images", true);
                        }
                      }}
                    />
                  </label>
                </div>
                {formik.errors.store_images && formik.touched.store_images && (
                  <span className="text-danger text-left d-block err-spn mt-1">
                    {formik.errors.store_images}
                  </span>
                )}
              </div>

              <div className="form-group my-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isAgreementSigned"
                    name="isAgreementSigned"
                    checked={formik.values.isAgreementSigned}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="isAgreementSigned" className="text-sm text-gray-700 m-0 cursor-pointer select-none">
                    {t("advertiser.business_profile.agree_terms", "I agree to the")}{" "}
                    <a
                      href="/page/advertiser-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-500 underline font-medium hover:text-red-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("advertiser.business_profile.advertiser_agreement", "Advertiser Agreement")}
                    </a>
                  </label>
                </div>
                <span className="text-danger text-left d-block err-spn mt-1">
                  {formik.errors.isAgreementSigned && formik.touched.isAgreementSigned && formik.errors.isAgreementSigned}
                </span>
              </div>

              <button
                type="submit"
                disabled={!formik.values.isAgreementSigned}
                className={`btn btn-pri w-full ${!formik.values.isAgreementSigned ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t("continue")}
              </button>
            </form>
          </div>
          {/* <div className="mt-8 text-center body-md text-zinc-700">
            {t("account_not_have")}
            <a
              href="#"
              className="headings-h6 bold text-[var(--secondary-400)] pl-[5px]"
              onClick={() => navigate('../signup')}
            >
              {t("sign_up")}
            </a>
          </div> */}
        </div>
      </section>
    </>
  );
}

export default ProfileSetup;
